// src/Components/TopicSelector.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom'; // 1. Add useLocation

// 1. Imports
import { getDocumentsByColumn } from '../../../Backend/SupabaseApis/commonService';
import { searchTopicsArray } from '../Utility/searchUtils';
import { EditorialCard } from '../Utility/EditorialCard';
// Assuming this is where you will store the new API function
import { fetchQuestions } from '../../../Backend/Test/questionService';

const TopicSelector = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { state } = location;
    const { title, chapterId } = location.state || {};

    const [allDocs, setAllDocs] = useState([]);
    const [displayedDocs, setDisplayedDocs] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    // --- UI STATE VARIABLES ---
    const [difficulty, setDifficulty] = useState('Basic');
    const [questionCount, setQuestionCount] = useState(5);

    // --- NEW SELECTION STATE ---
    const [selectedTopicIds, setSelectedTopicIds] = useState([]); // Stores list of IDs
    const [isGenerating, setIsGenerating] = useState(false); // Loading state for "Go" button

    useEffect(() => {
        if (!state || !state.title || !state.chapterId) {
            alert("Data Missing. Try Loading Again.");
            navigate(-1);
        } 
    }, [state, navigate]);

    // 2. Fetch Logic
    useEffect(() => {
        const fetchData = async () => {
            if (!chapterId) return;
            try {
                const data = await getDocumentsByColumn('topics', 'chapter_id', chapterId);
                setAllDocs(data);
                setDisplayedDocs(data);
            } catch (error) {
                console.error("Error loading topics:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [chapterId]);

    // 3. Search Logic
    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        const results = searchTopicsArray(allDocs, term);
        setDisplayedDocs(results);
    };

    // 4. Handle Question Count Change (Max 50)
    const handleQuestionCountChange = (e) => {
        let val = parseInt(e.target.value, 10);
        if (isNaN(val)) val = 1;
        if (val > 15) val = 15; // Backend restriction
        if (val < 1) val = 1;
        setQuestionCount(val);
    };

    // 5. Toggle Selection Logic
    const toggleTopicSelection = (sourceId) => {
        setSelectedTopicIds((prevIds) => {
            if (prevIds.includes(sourceId)) {
                // Remove if already selected
                return prevIds.filter(id => id !== sourceId);
            } else {
                // Check limit before adding
                if (prevIds.length >= 5) {
                    alert("You can only select up to 5 topics at a time.");
                    return prevIds;
                }
                // Add source_id to selection
                return [...prevIds, sourceId];
            }
        });
    };

    // 6. "GO" Button Logic
    // 6. "GO" Button Logic
    const handleStartTest = async () => {
        if (selectedTopicIds.length === 0) {
            alert("Please select at least one topic to start.");
            return;
        }
        setIsGenerating(true);

        try {
            const now = new Date();
            const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

            const nextTitle = `${title} • ${dateStr} ${timeStr}`;

            const { questions, testId } = await fetchQuestions(
                selectedTopicIds,
                difficulty,
                questionCount,
                nextTitle // Passing title here
            );
            navigate('/test', {
                state: {
                    questions: questions,
                    topicName: nextTitle,
                    testId: testId
                }
            });

        } catch (error) {
            console.error("Error generating test:", error);
            alert(`Failed to generate questions: ${error.message}`);
        } finally {
            setIsGenerating(false);
        }
    };

    if (!title || !chapterId) {
        return null;
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-xl font-semibold">Loading...</div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6 min-h-screen relative">

            {/* Overlay Loader for API Generation */}
            {isGenerating && (
                <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                        <p className="font-semibold text-gray-700">Generating Questions...</p>
                    </div>
                </div>
            )}

            {/* Header Section */}
            <div className="mb-6">
                <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-800 mb-2">
                    &larr; Back to Chapters
                </button>

                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">

                    <h1 className="text-2xl font-bold text-gray-800">
                        {title}
                    </h1>

                    {/* --- UI CONTROLS --- */}
                    <div className="flex flex-wrap items-center gap-3">

                        {/* Difficulty */}
                        <div className="bg-gray-200 p-1 rounded-lg flex items-center">
                            {['Basic', 'Medium', 'Hard'].map((level) => (
                                <button
                                    key={level}
                                    onClick={() => setDifficulty(level)}
                                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${difficulty === level
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    {level}
                                </button>
                            ))}
                        </div>

                        {/* Question Count Input */}
                        <div className="flex items-center bg-white border border-gray-200 rounded-lg px-3 py-1.5 shadow-sm">
                            <span className="text-sm text-gray-500 font-medium mr-2">
                                Questions:
                            </span>
                            <input
                                type="number"
                                min="1"
                                max="50"
                                value={questionCount}
                                onChange={handleQuestionCountChange}
                                className="w-12 text-sm font-bold text-gray-800 focus:outline-none text-center bg-transparent"
                            />
                        </div>

                        {/* --- NEW GO BUTTON --- */}
                        <button
                            onClick={handleStartTest}
                            disabled={selectedTopicIds.length === 0}
                            className={`px-6 py-1.5 rounded-lg font-bold text-white shadow-sm transition-all flex items-center gap-2
                                ${selectedTopicIds.length === 0
                                    ? 'bg-blue-300 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700 hover:shadow-md active:scale-95'
                                }`}
                        >
                            <span>GO</span>
                            {selectedTopicIds.length > 0 && (
                                <span className="bg-blue-500 text-xs py-0.5 px-2 rounded-full">
                                    {selectedTopicIds.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="mb-8 relative">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder="Search specific topics (e.g. Algebra)..."
                    className="w-full p-4 pl-12 rounded-lg bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg transition-all"
                />
                <svg
                    className="w-6 h-6 absolute left-4 top-4 text-gray-400"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {displayedDocs.map((doc, index) => {
                    const cardTitle = doc.topic_list ? doc.topic_list.join(", ") : "Untitled";
                    const isSelected = selectedTopicIds.includes(doc.source_id);

                    return (
                        // Wrapper Div for Selection Styling
                        <div
                            key={doc.id}
                            onClick={() => toggleTopicSelection(doc.source_id)}
                            className={`relative rounded-xl transition-all duration-200 cursor-pointer transform 
                                ${isSelected
                                    ? 'ring-4 ring-blue-500 scale-[1.02] shadow-lg'
                                    : 'hover:shadow-md hover:scale-[1.01]'
                                }`
                            }
                        >
                            {/* Visual Checkmark for Selected State */}
                            {isSelected && (
                                <div className="absolute -top-2 -right-2 z-10 bg-blue-600 text-white rounded-full p-1 shadow-md">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                                    </svg>
                                </div>
                            )}

                            {/* Pointer Events set to none on children so the wrapper handles the click */}
                            <div className="pointer-events-none">
                                <EditorialCard
                                    index={index}
                                    title={cardTitle}
                                // Removed the inner onClick, handled by wrapper
                                />
                            </div>
                        </div>
                    );
                })}

                {displayedDocs.length === 0 && (
                    <div className="col-span-full text-center text-gray-400 py-10">
                        No topics found matching "{searchTerm}"
                    </div>
                )}
            </div>
        </div>
    );
};

export default TopicSelector;
